import React, { createContext } from 'react';
import { StreamController } from '@/classes/StreamController';

export const RUNNING_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  REFINING: 'refining',
  ERROR: 'error',
};

const initialState = {
  resultId: null,
  answer: null,
  plan: null,
  context: null,
  sources: null,
  fullContext: null,
  runningStatus: RUNNING_STATUS.INACTIVE,
};

const REDUCER_ACTIONS = {
  open: 'open',
  messageReceived: 'messageReceived',
  errorReceived: 'errorReceived',
  refiningStart: 'refiningStart',
  complete: 'complete',
  reset: 'reset',
};

const completePartialJson = (str) => {
  let result = str.trim();
  const openBraces = (result.match(/{/g) || []).length;
  const closeBraces = (result.match(/}/g) || []).length;
  const openQuotes = (result.match(/"/g) || []).length;
  if (openQuotes % 2 !== 0) result += '"';
  for (let i = 0; i < openBraces - closeBraces; i++) result += '}';
  return result;
};

const parseReasoningMessage = (jsonString) => {
  try {
    const json = JSON.parse(jsonString);
    return { answer: json.answer, reasoning: json.reasoning };
  } catch (_) {
    try {
      const json = JSON.parse(completePartialJson(jsonString));
      return { answer: json.answer, reasoning: json.reasoning };
    } catch (error) {
      console.error('Error parsing reasoning message', error);
      return null;
    }
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case REDUCER_ACTIONS.open: {
      return { ...state, runningStatus: RUNNING_STATUS.RUNNING, resultId: action.payload };
    }
    case REDUCER_ACTIONS.errorReceived: {
      return { ...state, error: action.payload, runningStatus: RUNNING_STATUS.ERROR };
    }
    case REDUCER_ACTIONS.refiningStart: {
      return { ...state, runningStatus: RUNNING_STATUS.REFINING };
    }
    case REDUCER_ACTIONS.reset: {
      return initialState;
    }
    case REDUCER_ACTIONS.messageReceived: {
      const jsonString = action.payload;
      const message = parseReasoningMessage(jsonString);
      if (message) {
        return { ...state, answer: message.answer, reasoning: message.reasoning };
      } else {
        return state;
      }
    }
    case REDUCER_ACTIONS.complete: {
      const { payload } = action;
      const { answer: unparsedAnswer, plan, context, fullContext, sources } = payload;
      const { answer, reasoning } = parseReasoningMessage(unparsedAnswer);

      return {
        ...state,
        runningStatus: RUNNING_STATUS.COMPLETED,
        answer,
        reasoning,
        context,
        plan,
        fullContext,
        sources,
      };
    }

    default: {
      return state;
    }
  }
};

export const ReasoningResultContext = createContext(null);

export const ReasoningResultContextProvider = ({ resultId, children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const streamSource = React.useRef(null);

  React.useEffect(() => {
    new StreamController(resultId, { includeContext: true }, streamSource, dispatch, REDUCER_ACTIONS);
    return () => {
      streamSource?.current?.close();
      dispatch({ type: REDUCER_ACTIONS.reset });
    };
  }, [resultId]);

  const value = React.useMemo(() => ({ ...state }), [state]);

  return <ReasoningResultContext.Provider value={value}>{children}</ReasoningResultContext.Provider>;
};
